/**
 * partner-form.tsx — Ô nhập hồ sơ đối tác + thông tin nhận tiền (dùng ở đăng ký và trang Hồ sơ & nhận tiền).
 */
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/misc'
import { cn } from '@/lib/utils'
import type { RegisterForm } from '@/lib/affiliate'

type Errors = Partial<Record<keyof RegisterForm, string>>
type SetField = <K extends keyof RegisterForm>(k: K, v: RegisterForm[K]) => void

export function Field({ label, error, children, hint, className }: { label: string; error?: string; hint?: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label>{label}</Label>
      {children}
      {error ? <p className="text-xs text-destructive">{error}</p> : hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  )
}

export function PartnerTypePicker({ value, onChange, disabled }: { value: RegisterForm['partnerType']; onChange: (v: RegisterForm['partnerType']) => void; disabled?: boolean }) {
  const { t } = useTranslation('growth')
  const opts: { v: RegisterForm['partnerType']; label: string; hint: string }[] = [
    { v: 'individual', label: t('partnerForm.individual'), hint: t('partnerForm.individualHint') },
    { v: 'company', label: t('partnerForm.company'), hint: t('partnerForm.companyHint') },
  ]
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {opts.map((o) => (
        <button
          key={o.v}
          type="button"
          disabled={disabled}
          onClick={() => onChange(o.v)}
          className={cn(
            'rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
            value === o.v ? 'border-primary bg-primary/5' : 'hover:border-primary/40',
          )}
        >
          <p className="text-sm font-medium">{o.label}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{o.hint}</p>
        </button>
      ))}
    </div>
  )
}

export function ProfileFields({ f, set, errors }: { f: RegisterForm; set: SetField; errors: Errors }) {
  const { t } = useTranslation('growth')
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label={t('partnerForm.fullName')} error={errors.fullName}>
        <Input value={f.fullName} onChange={(e) => set('fullName', e.target.value)} autoComplete="name" />
      </Field>
      <Field label={t('partnerForm.phone')} error={errors.phone}>
        <Input value={f.phone} onChange={(e) => set('phone', e.target.value)} inputMode="tel" autoComplete="tel" />
      </Field>
      <Field label={t('partnerForm.email')} error={errors.email}>
        <Input value={f.email} onChange={(e) => set('email', e.target.value)} type="email" autoComplete="email" />
      </Field>
      <Field label={t('partnerForm.address')} error={errors.address}>
        <Input value={f.address} onChange={(e) => set('address', e.target.value)} autoComplete="street-address" />
      </Field>
      {f.partnerType === 'company' && (
        <>
          <Field label={t('partnerForm.companyName')} error={errors.companyName}>
            <Input value={f.companyName} onChange={(e) => set('companyName', e.target.value)} />
          </Field>
          <Field label={t('partnerForm.taxCode')} error={errors.taxCode}>
            <Input value={f.taxCode} onChange={(e) => set('taxCode', e.target.value)} inputMode="numeric" />
          </Field>
        </>
      )}
    </div>
  )
}

export function PayoutFields({ f, set, errors, allowCommitment08 }: { f: RegisterForm; set: SetField; errors: Errors; allowCommitment08: boolean }) {
  const { t } = useTranslation('growth')
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {f.partnerType === 'individual' && (
          <Field label={t('partnerForm.idNumber')} error={errors.idNumber} hint={t('partnerForm.idNumberHint')} className="sm:col-span-2">
            <Input value={f.idNumber} onChange={(e) => set('idNumber', e.target.value)} inputMode="numeric" autoComplete="off" />
          </Field>
        )}
        <Field label={t('partnerForm.bankName')} error={errors.bankName}>
          <Input value={f.bankName} onChange={(e) => set('bankName', e.target.value)} placeholder="VD: MB Bank, Vietcombank" />
        </Field>
        <Field label={t('partnerForm.bankBranch')}>
          <Input value={f.bankBranch} onChange={(e) => set('bankBranch', e.target.value)} />
        </Field>
        <Field label={t('partnerForm.bankAccount')} error={errors.bankAccount} hint={t('partnerForm.bankAccountHint')}>
          <Input value={f.bankAccount} onChange={(e) => set('bankAccount', e.target.value)} inputMode="numeric" autoComplete="off" />
        </Field>
        <Field label={t('partnerForm.bankAccountHolder')} error={errors.bankAccountHolder} hint={f.partnerType === 'company' ? t('partnerForm.bankAccountHolderHintCompany') : t('partnerForm.bankAccountHolderHintIndividual')}>
          <Input value={f.bankAccountHolder} onChange={(e) => set('bankAccountHolder', e.target.value.toUpperCase())} />
        </Field>
      </div>
      {f.partnerType === 'individual' && allowCommitment08 && (
        <label className="flex items-start gap-2 text-sm">
          <Checkbox checked={f.pitCommitment} onCheckedChange={(v) => set('pitCommitment', v === true)} className="mt-0.5" />
          <span>
            {t('partnerForm.commitment08Pre')} <b>{t('partnerForm.commitment08Bold')}</b> {t('partnerForm.commitment08Post')}
            <span className="block text-xs text-muted-foreground">{t('partnerForm.commitment08Note')}</span>
          </span>
        </label>
      )}
    </div>
  )
}
